import { useEffect, useRef, useState } from 'react';
import { Button } from 'components/Button';
import { useUser } from 'context/user-context';
import {
  OrderEditingIntegration,
  OrderEditingIntegrationInput,
  OrderEditingIntegrationResult,
} from 'types/order-editing';
import { cardClass, SettingToggle } from './WorkspaceSections';

export function FulfillmentProtection({
  integration,
  busy,
  onConfigure,
  onRefresh,
  onDirtyChange,
}: {
  integration?: OrderEditingIntegration;
  busy: boolean;
  onConfigure: (
    input: OrderEditingIntegrationInput,
  ) => Promise<OrderEditingIntegrationResult | undefined>;
  onRefresh: () => Promise<OrderEditingIntegration | undefined>;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { role, user } = useUser();
  const canConfigure =
    role === 'ADMIN' || user?.defaultRole === 'shipaid_admin';
  const [baseline, setBaseline] = useState(integration);
  const [holdUnpaid, setHoldUnpaid] = useState(
    integration?.holdUnpaid || false,
  );
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflict, setConflict] = useState(false);
  const [pending, setPending] = useState(false);
  const alive = useRef(true);
  const inFlight = useRef(false);
  const dirty = !!baseline && holdUnpaid !== baseline.holdUnpaid;
  const disabled = busy || pending;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );
  useEffect(() => {
    if (pending || !integration || integration.revision === baseline?.revision)
      return;
    if (!dirty) {
      setBaseline(integration);
      setHoldUnpaid(integration.holdUnpaid);
      setConflict(false);
      setError('');
    } else {
      setConflict(true);
      setError(
        'Fulfillment settings changed in another session. Refresh before saving again.',
      );
    }
  }, [integration, baseline?.revision, dirty, pending]);

  const refresh = async () => {
    if (disabled || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setNotice('');
    try {
      const refreshed = await onRefresh();
      if (alive.current && refreshed) {
        setBaseline(refreshed);
        setHoldUnpaid(refreshed.holdUnpaid);
        setConflict(false);
        setError('');
      }
    } finally {
      inFlight.current = false;
      if (alive.current) setPending(false);
    }
  };

  const save = async () => {
    if (
      !canConfigure ||
      !integration ||
      !baseline ||
      disabled ||
      inFlight.current ||
      conflict ||
      !dirty
    )
      return;
    inFlight.current = true;
    setPending(true);
    setError('');
    setNotice('');
    try {
      const result = await onConfigure({
        expectedRevision: baseline.revision,
        holdUnpaid,
        webhookUrl: baseline.webhookUrl || '',
        webhookEnabled: baseline.webhookEnabled,
        flowEnabled: baseline.flowEnabled,
      });
      if (!alive.current || !result) return;
      setBaseline(result.summary);
      setHoldUnpaid(result.summary.holdUnpaid);
      setNotice('Fulfillment setting saved.');
      await onRefresh();
    } catch (reason: any) {
      if (!alive.current) return;
      const changed =
        reason?.graphQLErrors?.some((item: any) =>
          String(item.extensions?.code).includes('CONFLICT'),
        ) || String(reason?.message || '').includes('CONFLICT');
      setConflict(true);
      setError(
        changed
          ? 'Fulfillment settings changed in another session. Refresh before saving again.'
          : 'Unable to save the fulfillment setting. Refresh the status before retrying.',
      );
    } finally {
      inFlight.current = false;
      if (alive.current) setPending(false);
    }
  };

  return (
    <section className={cardClass} aria-labelledby="fulfillment-holds-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="fulfillment-holds-heading" className="text-base font-semibold">
          Fulfillment holds
        </h2>
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={() => {
            void refresh();
          }}
        >
          Refresh fulfillment status
        </Button>
      </div>
      <p className="mt-2 text-sm text-gray-600">
        Required for item edits. SHIPAID holds fulfillment when a change needs
        additional payment. Your team must also pause fulfillment before
        approving physical item additions or swaps.
      </p>
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-800">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-3 text-sm text-green-800">
          {notice}
        </p>
      )}
      {!integration ? (
        <p className="mt-3 text-sm text-gray-600">
          Fulfillment status is unavailable. Refresh to try again.
        </p>
      ) : canConfigure ? (
        <>
          <SettingToggle
            label="Hold fulfillment while payment is due"
            description="Only SHIPAID-owned holds are managed. Uncertain payments require review."
            value={holdUnpaid}
            disabled={disabled || conflict}
            onChange={(value) => {
              setHoldUnpaid(value);
              setNotice('');
            }}
          />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              disabled={disabled || conflict || !dirty}
              onClick={() => {
                void save();
              }}
            >
              Save fulfillment setting
            </Button>
            <span className="text-xs text-gray-500">
              Saved separately from editing rules.
            </span>
          </div>
        </>
      ) : (
        <p className="mt-3 text-sm text-gray-600">
          Payment holds: {integration.holdUnpaid ? 'On' : 'Off'}. A store admin
          can change fulfillment holds.
        </p>
      )}
    </section>
  );
}
